import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Tag, PlusCircle, Search, List, Grid, Image, DollarSign, Info, Loader2, XCircle,
    Package, CalendarDays, RefreshCcw,
    ChevronDown, FolderPlus, Layers2, PackagePlus,
    Upload,
    UploadCloud
} from 'lucide-react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Checkbox from '../components/ui/Checkbox'; // Using your provided Checkbox component
import Dropdown from '../components/ui/Dropdown'; // Import the Dropdown component
import { useToast } from '../hooks/useToast';
import { fetchCatentries, fetchStaffAppropriateCatentries, API_ROOT_PATH, currencyCodeToSymbolMap, fetchStoresWhereStaffAssigned } from '../apiHelper';
import MassTaxUpdateModal from './MassTaxUpdateModal';

/**
 * ProductCard Component for Grid View.
 * Displays a single product item's details in a card format.
 */
const ProductCard = ({ product, onClick }) => {
    const attachmentUrl = product.attachments?.[0]?.attachment?.attachmenturl;
    const isSaleable = product.buyable === 1;
    // Check for expirydate and ensure it's not the default "0001-01-01" value
    const hasExpiryDate = product.expirydate && product.expirydate !== '0001-01-01';

    // Safely get list price and currency
    const listPrice = product.listprice?.listprice || 0;
    const currencyCode = product.listprice?.currency_rel?.setccurr || 'NGN'; // Default to NGN
    const currencySymbol = currencyCodeToSymbolMap[currencyCode] || currencyCode;

    return (
        <Card
            className="flex flex-col h-full overflow-hidden transition-all duration-200 ease-in-out hover:shadow-lg cursor-pointer hover:border-gmail-blue border"
            onClick={() => onClick(product)} // Handle click
        >
            <div className="relative w-full h-40 bg-gray-100 flex items-center justify-center rounded-t-lg overflow-hidden">
                {attachmentUrl ? (
                    <img
                        src={`${API_ROOT_PATH}${attachmentUrl}`}
                        alt={product.productname}
                        className="w-full h-full object-cover select-none pointer-events-none"
                        onContextMenu={(e) => e.preventDefault()}
                        draggable="false"
                    />
                ) : (
                    <Image size={64} className="text-gray-400" />
                )}
                <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-semibold
                            ${isSaleable ? 'bg-gmail-green text-white' : 'bg-gmail-red text-white'}
                            pointer-events-none`}>
                    {isSaleable ? 'For Sale' : 'Internal Use'}
                </div>
            </div>
            <div className="p-4 flex-1 flex flex-col justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-text-primary mb-1 truncate" title={product.productname}>
                        {product.productname}
                    </h3>
                    <p className="text-sm text-text-secondary mb-3 truncate" title={product.partnumber}>
                        Part No: {product.partnumber || 'N/A'}
                    </p>
                    <div className="flex items-center gap-2 text-text-primary text-sm mb-1">
                        <Tag size={16} className="text-gmail-blue" />
                        <span className="font-medium">
                            {product.catgpenrels?.[0]?.catalog?.identifier || 'No Catalog'}
                        </span>
                    </div>
                    <div className="flex items-center gap-2 text-text-primary text-sm mb-1">
                        <Tag size={16} className="text-purple-600" />
                        <span className="font-medium">
                            {product.catgpenrels?.[0]?.category?.identifier || 'No Category'}
                        </span>
                    </div>
                    <div className="flex items-center gap-2 text-text-primary text-sm mb-1">
                        <DollarSign size={16} className="text-gmail-green" />
                        <span className="font-medium">List Price: {currencySymbol}{listPrice.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-text-primary text-sm mb-1">
                        <Package size={16} className="text-gmail-yellow" />
                        <span className="font-medium">Qty Unit: {product.unitofmeasure?.name || 'N/A'}</span>
                    </div>
                    {hasExpiryDate && (
                        <div className="flex items-center gap-2 text-text-primary text-sm">
                            <CalendarDays size={16} className="text-orange-500" />
                            <span className="font-medium">Expiry: {new Date(product.expirydate).toLocaleDateString()}</span>
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
};

/**
 * ProductListItem Component for List View.
 * Displays a single product item's details in a table row format.
 */
const ProductListItem = ({ product, onClick }) => { // Added onClick prop
    const attachmentUrl = product.attachments?.[0]?.attachment?.attachmenturl;
    const isSaleable = product.buyable === 1;
    const hasExpiryDate = product.expirydate && product.expirydate !== '0001-01-01';

    const listPrice = product.listprice?.listprice || 0;
    const currencyCode = product.listprice?.currency_rel?.setccurr || 'NGN';
    const currencySymbol = currencyCodeToSymbolMap[currencyCode] || currencyCode;

    return (
        <tr
            className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors cursor-pointer"
            onClick={() => onClick(product)} // Handle click
        >
            <td className="px-4 py-3 w-20">
                <div className="w-16 h-16 bg-gray-100 flex items-center justify-center rounded-md overflow-hidden">
                    {attachmentUrl ? (
                        <img
                            src={`${API_ROOT_PATH}${attachmentUrl}`}
                            alt={product.productname}
                            className="w-full h-full object-cover pointer-events-none"
                        />
                    ) : (
                        <Image size={32} className="text-gray-400" />
                    )}
                </div>
            </td>
            <td className="px-4 py-3">
                <p className="font-semibold text-text-primary">{product.productname}</p>
                <p className="text-xs text-text-secondary">Part No: {product.partnumber || 'N/A'}</p>
            </td>
            <td className="px-4 py-3 text-center">
                <span className="font-medium">
                    {product.catgpenrels?.[0]?.catalog?.identifier || 'N/A'}
                </span>
            </td>
            <td className="px-4 py-3 text-center">
                <span className="font-medium">
                    {product.catgpenrels?.[0]?.category?.identifier || 'N/A'}
                </span>
            </td>
            <td className="px-4 py-3 text-center">
                <span className="font-bold text-text-primary">{currencySymbol}{listPrice.toFixed(2)}</span>
            </td>
            <td className="px-4 py-3 text-center">
                <span className={`${isSaleable ? 'text-green-600' : 'text-red-600'} font-medium`}>
                    {isSaleable ? 'Yes' : 'No'}
                </span>
            </td>
            <td className="px-4 py-3 text-center">
                {hasExpiryDate ? new Date(product.expirydate).toLocaleDateString() : 'N/A'}
            </td>
        </tr>
    );
};

/**
 * ProductCatalogs Component
 * This component displays a comprehensive overview and management interface for product catalog entries.
 * It includes filters, search, and a detailed table or grid view of entries.
 */
const ProductCatalogs = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [user, setUser] = useState(null);
    const [catalogEntries, setCatalogEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCatalogs, setSelectedCatalogs] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

    const [selectedStoreId, setSelectedStoreId] = useState('');
    const [stores, setStores] = useState([]);

    const [offset, setOffset] = useState(0);
    const [limit] = useState(100);
    const [hasMore, setHasMore] = useState(true);

    const [allCatalogs, setAllCatalogs] = useState([]);
    const [allCategories, setAllCategories] = useState([]);

    const [isMassTaxModalOpen, setIsMassTaxModalOpen] = useState(false);

    const initializeStores = async (parsedUser) => {
        try {
            const fetchedStores = await fetchStoresWhereStaffAssigned(parsedUser);
            const options = fetchedStores
            // .filter(store => store.storetype !== 'WHS')
            .map(store => ({
                label: store.identifier,
                value: store.store_id,
            }))
            setStores(options);

            if (fetchedStores.length > 0) {
                const defaultStoreId = fetchedStores[0].store_id;
                setSelectedStoreId(defaultStoreId);
                // trigger the initial load immediately with the ID 
                // we just found to bypass the state update delay.
                loadCatalogEntries(0, false, defaultStoreId);
            } else {
                // if not stores, just load normally
                loadCatalogEntries(0, false);
            }
        } catch (err) {
            console.error(err);
            showToast(err.response?.data?.detail || "Failed to load assigned stores", "error");
        }
    };

    // Effect to load user from local storage
    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast("Please log in to view product catalogs.", "info");
            navigate('/login')
        } else {
            const parsedUser = JSON.parse(lcUser)
            setUser(parsedUser);
            initializeStores(parsedUser);
        }
    }, [showToast]);

    // Function to extract unique catalogs and categories from fetched entries
    const extractUniqueFilters = useCallback((entries) => {
        const uniqueCatalogs = new Map(); // Map to store unique catalogs by ID
        const uniqueCategories = new Map(); // Map to store unique categories by ID

        entries.forEach(entry => {
            if (entry.catgpenrels && entry.catgpenrels.length > 0) {
                entry.catgpenrels.forEach(rel => {
                    if (rel.catalog && !uniqueCatalogs.has(rel.catalog.catalog_id)) {
                        uniqueCatalogs.set(rel.catalog.catalog_id, {
                            id: rel.catalog.catalog_id,
                            name: rel.catalog.identifier
                        });
                    }
                    if (rel.category && !uniqueCategories.has(rel.category.category_id)) {
                        uniqueCategories.set(rel.category.category_id, {
                            id: rel.category.category_id,
                            name: rel.category.identifier
                        });
                    }
                });
            }
        });

        setAllCatalogs(Array.from(uniqueCatalogs.values()));
        setAllCategories(Array.from(uniqueCategories.values()));
    }, []);

    // Fetch catalog entries
    const loadCatalogEntries = useCallback(async (currentOffset = 0, append = false, overrideStoreId = null) => {
        if (!user) return;
        setLoading(true);
        setError(null);
        try {
            const activeStoreId = overrideStoreId !== null ? overrideStoreId : selectedStoreId;
            const params = activeStoreId ? {...user, store_id: Number(activeStoreId)} : user;
            // console.log("Fetching with params:", params);
            const fetchedEntries = await fetchStaffAppropriateCatentries(params);
            // console.log(fetchedEntries)
            if (append) {
                setCatalogEntries(prevEntries => [...prevEntries, ...fetchedEntries]);
            } else {
                setCatalogEntries(fetchedEntries);
            }
            setHasMore(fetchedEntries.length === limit);
            if (currentOffset === 0) { // Only extract filters on initial load
                extractUniqueFilters(fetchedEntries);
            }
        } catch (err) {
            console.error("Error loading catalog entries:", err);
            const errorMessage = err.response?.data?.detail || `Failed to load catalog entries: ${err.message || err}`;
            setError(errorMessage);
            showToast(errorMessage, "error");
        } finally {
            setLoading(false);
        }
    }, [user, limit, selectedStoreId, showToast, extractUniqueFilters]);

    useEffect(() => {
        if (user) {
            setOffset(0);
            loadCatalogEntries(0, false);
        }
    }, [selectedStoreId]);

    const handleLoadMore = () => {
        setOffset(prevOffset => prevOffset + limit);
        loadCatalogEntries(offset + limit, true);
    };

    const handleCatalogFilterChange = useCallback((catalogId, isChecked) => {
        setSelectedCatalogs(prev => {
            const newState = isChecked ? [...prev, catalogId] : prev.filter(id => id !== catalogId);
            // console.log("Selected Catalogs after change:", newState); // Debugging
            return newState;
        });
        setOffset(0); // Reset pagination on filter change
    }, []);

    const handleCategoryFilterChange = useCallback((categoryId, isChecked) => {
        setSelectedCategories(prev => {
            const newState = isChecked ? [...prev, categoryId] : prev.filter(id => id !== categoryId);
            // console.log("Selected Categories after change:", newState); // Debugging
            return newState;
        });
        setOffset(0); // Reset pagination on filter change
    }, []);

    // Handle reset filters
    const handleResetFilters = useCallback(() => {
        setSearchTerm('');
        setSelectedCatalogs([]);
        setSelectedCategories([]);
        setSelectedStoreId('');
        setOffset(0); // Reset pagination
        showToast("Filters reset.", "info");
    }, [showToast]);


    // Filtered catalog entries based on search and selected filters
    const filteredAndSortedEntries = useMemo(() => {
        // console.log("Filtering entries. SearchTerm:", searchTerm, "Selected Catalogs:", selectedCatalogs, "Selected Categories:", selectedCategories); // Debugging
        let filtered = catalogEntries;

        if (searchTerm) {
            const lowerCaseSearchTerm = searchTerm.toLowerCase();
            filtered = filtered.filter(entry =>
                entry.productname.toLowerCase().includes(lowerCaseSearchTerm) ||
                (entry.partnumber && entry.partnumber.toLowerCase().includes(lowerCaseSearchTerm))
            );
        }

        if (selectedCatalogs.length > 0) {
            filtered = filtered.filter(entry =>
                entry.catgpenrels?.some(rel => selectedCatalogs.includes(rel.catalog?.catalog_id))
            );
        }

        if (selectedCategories.length > 0) {
            filtered = filtered.filter(entry =>
                entry.catgpenrels?.some(rel => selectedCategories.includes(rel.category?.category_id))
            );
        }
        // console.log("Filtered entries count:", filtered.length); // Debugging

        // Sort by product name
        return filtered.sort((a, b) => a.productname.localeCompare(b.productname));
    }, [catalogEntries, searchTerm, selectedCatalogs, selectedCategories]);

    // Data preparation for all products
    const allProductData = useMemo(() => {
        return catalogEntries.map(entry => ({
            catentry_id: entry.catentry_id,
            partnumber: entry.partnumber,
            productname: entry.productname,
            catalog_identifier: entry.catgpenrels?.[0]?.catalog?.identifier || 'N/A',
            category_identifier: entry.catgpenrels?.[0]?.category?.identifier || 'N/A',
        }))
    }, [catalogEntries])

    // Handle click on a product item
    const handleProductClick = useCallback((product) => {
        const selectedId = product.catentry_id;

        // navigate to ItemDetails.jsx, passing the required data
        navigate('/catalog/item-details', {
            state: {
                users_id: user.users_id,
                selectedCatentryId: selectedId,
                allCatalogEntries: allProductData
            }
        });
    }, [navigate, allProductData, user]);

    // DataTable2 Columns for List View
    const productTableColumns = useMemo(() => [
        {
            key: 'image',
            label: 'Image',
            render: (value, row) => {
                const attachmentUrl = row.attachments?.[0]?.attachment?.attachmenturl;
                return (
                    <div className="w-16 h-16 bg-gray-100 flex items-center justify-center rounded-md overflow-hidden">
                        {attachmentUrl ? (
                            <img
                                src={`${API_ROOT_PATH}${attachmentUrl}`}
                                alt={row.productname}
                                className="w-full h-full object-cover pointer-events-none"
                            />
                        ) : (
                            <Image size={32} className="text-gray-400" />
                        )}
                    </div>
                );
            }
        },
        {
            key: 'productname',
            label: 'Product Name'
        },
        {
            key: 'catalog',
            label: 'Catalog',
            render: (value, row) => row.catgpenrels?.[0]?.catalog?.identifier || 'N/A'
        },
        {
            key: 'category',
            label: 'Category',
            render: (value, row) => row.catgpenrels?.[0]?.category?.identifier || 'N/A'
        },
        {
            key: 'listprice.listprice',
            label: 'List Price',
            render: (value, row) => {
                const currencyCode = row.listprice?.currency_rel?.setccurr || 'NGN';
                const currencySymbol = currencyCodeToSymbolMap[currencyCode] || currencyCode;
                return `${currencySymbol}${parseFloat(value || 0).toFixed(2)}`;
            }
        },
        {
            key: 'buyable',
            label: 'For Sale',
            render: (value) => (value === 1 ? 'Yes' : 'No')
        },
        {
            key: 'expirydate',
            label: 'Expiry Date',
            render: (value) => (value && value !== '0001-01-01' ? new Date(value).toLocaleDateString() : 'N/A')
        },
    ], []);


    if (!user) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-orange-600'>
                <Info size={32} className="mb-2" />
                <p className="text-lg mb-4">Please log in to view product catalogs.</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col lg:flex-row h-[calc(100vh-100px)] bg-gray-100 p-6 rounded-lg shadow-inner gap-6">
            {/* Left Sidebar for Filters */}
            <Card className="w-full lg:w-1/5 bg-white rounded-lg shadow-md p-6 flex flex-col overflow-y-auto custom-scrollbar"> {/* Adjusted width */}
                <h2 className="text-xl font-bold text-text-primary mb-4 flex items-center gap-2">
                    <Search size={20} className="text-gmail-blue" /> Filters
                </h2>

                <div className="mb-6 pb-6 border-b border-gray-100">
                    {/* <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <Layers2 size={18} className="text-gmail-blue" /> Branch
                    </h3> */}
                    <select 
                        value={selectedStoreId}
                        onChange={(e) => setSelectedStoreId(e.target.value)}
                        className="w-full p-2 text-sm border border-gray-300 rounded-md focus:ring-gmail-blue focus:border-gmail-blue"
                    >
                        <option value="">All / Default</option>
                        {stores.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>

                <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-700 mb-3">Catalogs</h3>
                    {allCatalogs.length === 0 ? (
                        <p className="text-sm text-gray-500">No catalogs available.</p>
                    ) : (
                        <div className="space-y-2">
                            {allCatalogs.map(catalog => (
                                <Checkbox
                                    key={catalog.id}
                                    id={`catalog-${catalog.id}`}
                                    label={catalog.name}
                                    checked={selectedCatalogs.includes(catalog.id)}
                                    onChange={(e) => handleCatalogFilterChange(catalog.id, e.target.checked)}
                                    containerClassName="mb-0" // Override default mb-4 from Checkbox
                                />
                            ))}
                        </div>
                    )}
                </div>

                <div>
                    <h3 className="text-lg font-semibold text-gray-700 mb-3">Categories</h3>
                    {allCategories.length === 0 ? (
                        <p className="text-sm text-gray-500">No categories available.</p>
                    ) : (
                        <div className="space-y-2">
                            {allCategories.map(category => (
                                <Checkbox
                                    key={category.id}
                                    id={`category-${category.id}`}
                                    label={category.name}
                                    checked={selectedCategories.includes(category.id)}
                                    onChange={(e) => handleCategoryFilterChange(category.id, e.target.checked)}
                                    containerClassName="mb-0" // Override default mb-4 from Checkbox
                                />
                            ))}
                        </div>
                    )}
                </div>
            </Card>

            {/* Right Main Content Area */}
            <div className="flex flex-col w-full lg:w-4/5 bg-white rounded-lg shadow-md p-6"> {/* Adjusted width */}
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-3xl font-bold text-text-primary flex items-center gap-2">
                        <Tag size={28} className="text-gmail-blue" /> Product Catalogs
                    </h1>
                    <Dropdown
                        trigger={
                            <Button variant="primary" className="flex items-center gap-2">
                                <PlusCircle size={20} /> Create New <ChevronDown size={20} />
                            </Button>
                        }
                        align="right"
                    >
                        <div className="py-1">
                            <button
                                onClick={() => navigate('/catalog/create-item')}
                                className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                                <Tag size={16} /> New Catalog Entry
                            </button>
                            <button
                                onClick={() => navigate('/catalog/create-catalogs')}
                                className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                                <FolderPlus size={16} /> New Catalog
                            </button>
                            <button
                                onClick={() => navigate('/catalog/create-categories')}
                                className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                                <FolderPlus size={16} /> New Category
                            </button>
                            <button
                                onClick={() => navigate('/catalog/create-package')}
                                className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                                <PackagePlus size={16} /> New Package
                            </button>
                            <button
                                onClick={() => navigate('/import-data', {
                                    state: {
                                        target: 'batchproducts'
                                    }
                                })}
                                className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                                <UploadCloud size={16} /> Batch Import
                            </button>                            
                            <button
                                onClick={() => navigate('/import-data', {
                                    state: {
                                        target: 'batchrawmaterials'
                                    }
                                })}
                                className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                                <UploadCloud size={16} /> Raw Materials
                            </button>
                            
                            <button
                                onClick={() => navigate('/import-data', {
                                    state: {
                                        target: 'batchcategories'
                                    }
                                })}
                                className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                                <UploadCloud size={16} /> Import Categories
                            </button>
                            <hr className="my-2 border-gray-200" />
                            <button
                                onClick={() => setIsMassTaxModalOpen(true)}
                                className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm font-sm text-gray-700 hover:bg-gray-100"
                            >
                                <DollarSign size={16} /> Apply Tax Rules
                            </button>
                        </div>
                    </Dropdown>
                </div>

                <div className="flex items-center justify-between mb-4 gap-4">
                    <Input
                        type="text"
                        placeholder="Search products by name or part number..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="flex-1 pl-10 pr-4 py-2 text-sm"
                        icon={<Search size={18} className="text-gray-dark absolute left-3 top-1/2 -translate-y-1/2" />}
                    />
                    <div className="flex gap-2">
                        <Button
                            variant={viewMode === 'grid' ? 'primary' : 'secondary'}
                            onClick={() => setViewMode('grid')}
                            size="sm"
                            className="p-2"
                        >
                            <Grid size={20} />
                        </Button>
                        <Button
                            variant={viewMode === 'list' ? 'primary' : 'secondary'}
                            onClick={() => setViewMode('list')}
                            size="sm"
                            className="p-2"
                        >
                            <List size={20} />
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={handleResetFilters}
                            size="sm"
                            className="p-2"
                            title="Reset Filters"
                        >
                            <RefreshCcw size={20} />
                        </Button>
                    </div>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
                    {loading && filteredAndSortedEntries.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 text-text-secondary">
                            <Loader2 className="animate-spin h-8 w-8 mb-3 text-gmail-blue" />
                            <p>Loading catalog entries...</p>
                        </div>
                    ) : error ? (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-4" role="alert">
                            <p>{error}</p>
                            <Button onClick={() => loadCatalogEntries(0, false)} variant="secondary" className="mt-2">Retry Loading</Button>
                        </div>
                    ) : filteredAndSortedEntries.length === 0 ? (
                        <div className="p-8 text-center text-text-secondary">
                            <Info size={48} className="mx-auto mb-4 text-gray-400" />
                            <p className="text-lg">No catalog entries found matching your criteria.</p>
                            <p className="text-sm mt-2">Try adjusting your filters or search term, or create a new entry.</p>
                            <Button onClick={() => navigate('/catalog/create-item')} variant="secondary" className="mt-4"> {/* Updated path here */}
                                Add New Catalog Entry
                            </Button>
                        </div>
                    ) : (
                        <>
                            {viewMode === 'grid' ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                                    {filteredAndSortedEntries.map(entry => (
                                        <ProductCard key={entry.catentry_id} product={entry} onClick={handleProductClick} />
                                    ))}
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full text-sm text-text-primary">
                                        <thead>
                                            <tr>
                                                {productTableColumns.map(col => (
                                                    <th key={col.key} className="px-4 py-3 text-left font-semibold text-gray-700 whitespace-nowrap">
                                                        {col.label}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredAndSortedEntries.map(entry => (
                                                <ProductListItem key={entry.catentry_id} product={entry} onClick={handleProductClick} />
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            {hasMore && (
                                <div className="flex justify-center mt-6">
                                    <Button
                                        onClick={handleLoadMore}
                                        disabled={loading}
                                        variant="secondary"
                                        className="px-6 py-3"
                                    >
                                        {loading ? 'Loading More...' : 'Load More Entries'}
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
            {isMassTaxModalOpen && (
                <MassTaxUpdateModal
                    user={user}
                    onClose={() => setIsMassTaxModalOpen(false)}
                />
            )}
        </div>
    );
};

export default ProductCatalogs;
